import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerAuthContext = createContext(null);

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [customerToken, setCustomerToken] = useState(localStorage.getItem('customerToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customerToken) {
      fetchCustomer();
    } else {
      setLoading(false);
    }
  }, [customerToken]);

  const getAuthHeader = () => {
    if (customerToken) {
      return { Authorization: `Bearer ${customerToken}` };
    }
    return {};
  };

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/me`, {
        headers: { Authorization: `Bearer ${customerToken}` }
      });
      setCustomer(response.data);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/customers/register`, data);
    const { access_token, customer: customerData } = response.data;
    
    localStorage.setItem('customerToken', access_token);
    setCustomerToken(access_token);
    setCustomer(customerData);
    
    return customerData;
  };

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API}/customers/login`, { email, password, remember_me: rememberMe });
    const { access_token, customer: customerData } = response.data;
    
    localStorage.setItem('customerToken', access_token);
    setCustomerToken(access_token);
    setCustomer(customerData);
    
    return customerData;
  };

  const logout = () => {
    localStorage.removeItem('customerToken');
    setCustomerToken(null);
    setCustomer(null);
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/customers/me`, data, {
      headers: { Authorization: `Bearer ${customerToken}` }
    });
    setCustomer(response.data);
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.post(`${API}/customers/change-password`, null, {
      params: { current_password: currentPassword, new_password: newPassword },
      headers: { Authorization: `Bearer ${customerToken}` }
    });
  };

  const getOrders = async () => {
    const response = await axios.get(`${API}/customers/orders`, {
      headers: { Authorization: `Bearer ${customerToken}` }
    });
    return response.data;
  };

  return (
    <CustomerAuthContext.Provider value={{ 
      customer, 
      customerToken,
      loading, 
      register,
      login, 
      logout, 
      updateProfile,
      changePassword,
      getOrders,
      getAuthHeader,
      isAuthenticated: !!customer
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
